import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { IconButton, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import NewSelect from "../Inputs/NewSelect.jsx";
import NewText from "../Inputs/NewText.jsx";
import ParcelNumbersField from "../Inputs/ParcelNumbersField.jsx";
import RadioGroupField from "../Inputs/RadioGroupField";
import MapComponent from "../Map/NewMap.jsx";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";
import "./styles/projectlocation.css";

// Map Instructions Component
const MapInstructions = () => {
  return (
    <div id="map-instructions">
      <h1 id="map-instructions-heading">Map & Pin Instructions</h1>
      <div className="instructions">
        <div className="instruction">
          <h3 className="instruction-name">Moving Around the Map</h3>
          <p className="map-instructions">
            To move around the map, click and drag the map or pressing the full
            screen button on the top right and press an arrow key in the
            direction you want to move.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Zoom</h3>
          <p className="map-instructions">
            To can zoom in and out of the map by scrolling up and down with your
            mouse or by pressing the plus and minus buttons on the bottom right
            of the map.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">
            Adding/Removing a Pin (Non-Linear)
          </h3>
          <p className="map-instructions">
            Click on the map where you would like to add the pin. Click on the
            pin to remove it from the map. You can move the pin by either
            clicking and removing the pin and then clicking again on the new
            location or by selecting a new area of the map to place the pin.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Adding/Removing a Pin (Linear)</h3>
          <p className="map-instructions">
            Click on the map where you would like to add the starting pin. Click
            again anywhere on the map to add the ending pin. Click either pin to
            remove it from the map. You can only move a specific pin by first
            clicking and removing the pin and then clicking again on the new
            location.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Incorrect Address</h3>
          <p className="map-instructions">
            If the pin provides an incorrect address, change the address in the
            field provided below the map.
          </p>
        </div>
      </div>
    </div>
  );
};

// Slide 6: Project Location
const ProjectLocation = ({ setSlideStatus, slideState, formStateLoaded }) => {
  const {
    slide1,
    slide9,
    setSlide9,
    setIsLoading,
    requestStatus,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
    disableAll,
  } = useContext(FormContext);

  const { clientId, projectId } = useContext(DashboardContext);

  const [showInstructions, setShowInstructions] = useState(false);
  const [disabledInputs, setDisabledInputs] = useState([]);
  const [counties, setCounties] = useState([
    "Clark",
    "Nye",
    "Washoe",
    "Carson City",
    "Douglas",
    "Lyon",
    "Storey",
    "Churchill",
    "Elko",
    "Esmeralda",
    "Eureka",
    "Humboldt",
    "Lander",
    "Lincoln",
    "Mineral",
    "Pershing",
    "White Pine",
  ]);
  const [cities, setCities] = useState(
    [
      "Las Vegas",
      "Henderson",
      "Reno",
      "Sparks",
      "Carson City",
      "Boulder City",
      "North Las Vegas",
      "Mesquite",
      "Elko",
      "Fernley",
      "Fallon",
      "Winnemucca",
      "Pahrump",
    ].sort(),
  );

  useEffect(() => {
    scrollToFirstRequiredField(slide9);
  }, []);

  // Update Slide Logic
  const slide9Mutation = useSetSlide(
    clientId,
    projectId,
    9,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  const [currParcelNumber, setCurrParcelNumber] = useState("");
  const [parcelNumbersList, setParcelNumbersList] = useState(
    slide9 && slide9.parcelNumbers ? slide9.parcelNumbers.split(", ") : [],
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "9",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  // Create rows state
  const [rows, setRows] = useState([]);

  // Add data to rows state
  useEffect(() => {
    if (slide9.isLinear === "Yes") {
      const row1 = {
        id: "marker-1",
        marker: "Green",
        address: slide9.pAdd || slide9.marker.details[0],
        county: slide9.pCounty || slide9.marker.details[1],
        city: slide9.pCity || slide9.marker.details[2],
        zip: slide9.pZip || slide9.marker.details[3],
        latitude: slide9.bLat || slide9.marker.position[0],
        longitude: slide9.bLng || slide9.marker.position[1],
      };
      const row2 = {
        id: "marker-2",
        marker: "Red",
        address: slide9.marker2.details[0],
        county: slide9.marker2.details[1],
        city: slide9.marker2.details[2],
        zip: slide9.marker2.details[3],
        latitude: slide9.eLat || slide9.marker2.position[0],
        longitude: slide9.eLng || slide9.marker2.position[1],
      };

      setRows([row1, row2]);
    } else {
      const row = {
        id: "marker-1",
        marker: "Green",
        address: slide9.pAdd || slide9.marker.details[0],
        county: slide9.pCounty || slide9.marker.details[1],
        city: slide9.pCity || slide9.marker.details[2],
        zip: slide9.pZip || slide9.marker.details[3],
        latitude: slide9.bLat || slide9.marker.position[0],
        longitude: slide9.bLng || slide9.marker.position[1],
      };

      setRows([row]);
    }
  }, [slide9]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <RadioGroupField
            label={"Is this a linear project?"}
            idProp={"isLinear"}
            val={slide9.isLinear}
            state={slide9}
            setState={setSlide9}
            debouncedSave={slide9Mutation}
            setIsLoading={setIsLoading}
            disableInput={disabledInputCheck("isLinear", disabledInputs)}
          />
          {showInstructions ? <MapInstructions /> : null}
          <header id="map-header">
            <h3 id="map-heading">
              {slide9.isLinear === "No" || slide9.isLinear === ""
                ? "Click to the centermost point of the project"
                : "Click & Select the estimated beginning and ending points of the project"}
            </h3>
            <IconButton
              id="map-instructions-button"
              onClick={() => {
                setShowInstructions(!showInstructions);
              }}
            >
              <Tooltip
                id="rg-tooltip"
                title="Click to view/hide map instructions"
                classes={{
                  tooltip: "radio-group-tooltip",
                  popper: "radio-group-popper",
                  arrow: "radio-group-arrow",
                  tooltipPlacementRight: "radio-group-right",
                }}
                arrow
                placement="right"
              >
                <HelpOutlineIcon id="help-icon" />
              </Tooltip>
            </IconButton>
          </header>
          <MapComponent
            projectName={slide1.projectName}
            slide9={slide9}
            setSlide9={setSlide9}
            requestStatus={requestStatus}
            disableAll={disableAll}
            debouncedSave={slide9Mutation}
            setIsLoading={setIsLoading}
          />
          <div id="location-inputs">
            {!slide9.pAdd ||
            !slide9.pCity ||
            !slide9.pCounty ||
            !slide9.pZip ? (
              <p className="important-text">
                Please Enter the Following Information
              </p>
            ) : null}
            <ResponsiveRow type="default">
              <NewText
                label={"Closest Street Address"}
                idProp={"pAdd"}
                val={slide9.pAdd}
                state={slide9}
                setState={setSlide9}
                debouncedSave={slide9Mutation}
                setIsLoading={setIsLoading}
                req={!slide9.pAdd || slide9.pAdd.length < 10}
                type="address"
                disabledInput={disabledInputCheck("pAdd", disabledInputs)}
                disabledInputMessage={
                  disabledInputCheck("pAdd", disabledInputs) === false
                    ? getInputMessage("pAdd", invalidInputsData, "9")
                    : ""
                }
                invalidData={getInvalidData("pAdd", invalidInputsData, "9")}
              />
              <NewText
                label={"City"}
                idProp={"pCity"}
                type="city"
                val={slide9.pCity}
                state={slide9}
                setState={setSlide9}
                debouncedSave={slide9Mutation}
                setIsLoading={setIsLoading}
                autoData={cities}
                req={
                  slide9.pCity === "" || cities.includes(slide9.pCity) === false
                }
                disabledInput={disabledInputCheck("pCity", disabledInputs)}
                disabledInputMessage={
                  disabledInputCheck("pCity", disabledInputs) === false
                    ? getInputMessage("pCity", invalidInputsData, "9")
                    : ""
                }
                invalidData={getInvalidData("pCity", invalidInputsData, "9")}
              />
              <NewText
                label={"Zip"}
                idProp={"pZip"}
                type="zipCode"
                val={slide9.pZip}
                state={slide9}
                setState={setSlide9}
                debouncedSave={slide9Mutation}
                setIsLoading={setIsLoading}
                req={!slide9.pZip || slide9.pZip.length !== 5}
                disabledInput={disabledInputCheck("pZip", disabledInputs)}
                disabledInputMessage={
                  disabledInputCheck("pZip", disabledInputs) === false
                    ? getInputMessage("pZip", invalidInputsData, "9")
                    : ""
                }
                invalidData={getInvalidData("pZip", invalidInputsData, "9")}
              />
              <NewText
                label={"County"}
                idProp={"pCounty"}
                val={slide9.pCounty}
                state={slide9}
                setState={setSlide9}
                debouncedSave={slide9Mutation}
                setIsLoading={setIsLoading}
                autoData={counties}
                req={
                  slide9.pCounty === "" ||
                  counties.includes(slide9.pCounty) === false
                }
                disabledInput={disabledInputCheck("pCounty", disabledInputs)}
                disabledInputMessage={
                  disabledInputCheck("pCounty", disabledInputs) === false
                    ? getInputMessage("pCounty", invalidInputsData, "9")
                    : ""
                }
                invalidData={getInvalidData("pCounty", invalidInputsData, "9")}
                type="county"
              />
            </ResponsiveRow>
          </div>
          <h2 className="question-section">Major Cross Streets</h2>
          <ResponsiveRow type="center">
            <NewText
              label={"Cross Road 1"}
              idProp={"crossRoad1"}
              val={slide9.crossRoad1}
              state={slide9}
              setState={setSlide9}
              debouncedSave={slide9Mutation}
              setIsLoading={setIsLoading}
              req={!slide9.crossRoad1 || slide9.crossRoad1.length < 5}
              disabledInput={disabledInputCheck("crossRoad1", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("crossRoad1", disabledInputs) === false
                  ? getInputMessage("crossRoad1", invalidInputsData, "9")
                  : ""
              }
              invalidData={getInvalidData("crossRoad1", invalidInputsData, "9")}
              type="city"
            />
            <NewText
              label={"Cross Road 2"}
              idProp={"crossRoad2"}
              val={slide9.crossRoad2}
              state={slide9}
              setState={setSlide9}
              debouncedSave={slide9Mutation}
              setIsLoading={setIsLoading}
              req={!slide9.crossRoad2 || slide9.crossRoad2.length < 5}
              disabledInput={disabledInputCheck("crossRoad2", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("crossRoad2", disabledInputs) === false
                  ? getInputMessage("crossRoad2", invalidInputsData, "9")
                  : ""
              }
              invalidData={getInvalidData("crossRoad2", invalidInputsData, "9")}
              type="city"
            />
            <NewSelect
              idProp="cornerDirection"
              label="Direction"
              type={"cornerDirection"}
              val={slide9.cornerDirection}
              state={slide9}
              setState={setSlide9}
              debouncedSave={slide9Mutation}
              setIsLoading={setIsLoading}
              req={slide9.cornerDirection === ""}
              disabledInput={disabledInputCheck(
                "cornerDirection",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("cornerDirection", disabledInputs) === false
                  ? getInputMessage("cornerDirection", invalidInputsData, "9")
                  : ""
              }
              invalidData={getInvalidData(
                "cornerDirection",
                invalidInputsData,
                "9",
              )}
            />
          </ResponsiveRow>
          <h2 className="question-section">Parcel Numbers</h2>
          <ResponsiveRow type="center">
            <div id="add-parcel-container">
              <ParcelNumbersField
                idProp="parcelNumbers"
                label="Parcel Number"
                req={!slide9.parcelNumbers}
                slide9={slide9}
                state={currParcelNumber}
                setState={setCurrParcelNumber}
                disabledInput={disabledInputCheck(
                  "parcelNumbers",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("parcelNumbers", disabledInputs) === false
                    ? getInputMessage("parcelNumbers", invalidInputsData, "8")
                    : ""
                }
                invalidData={getInvalidData(
                  "parcelNumbers",
                  invalidInputsData,
                  "8",
                )}
              />
              <div id="add-parcel-number">
                <Button
                  id="add-parcel"
                  className="add-parcel-btn"
                  variant="contained"
                  disabled={currParcelNumber.length < 14 ? true : false}
                  onClick={() => {
                    if (parcelNumbersList.includes(currParcelNumber)) {
                      setCurrParcelNumber("");
                      return;
                    }
                    let newParcelNumbers = [
                      ...parcelNumbersList,
                      currParcelNumber,
                    ];
                    setParcelNumbersList(newParcelNumbers);
                    let newState = {
                      ...slide9,
                      parcelNumbers: newParcelNumbers.join(", "),
                    };
                    setSlide9(newState);

                    setIsLoading(true);
                    slide9Mutation.mutate(newState);

                    setCurrParcelNumber("");
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </ResponsiveRow>
          <ResponsiveRow type="closer-center">
            {parcelNumbersList.map((parcel, index) => (
              <Chip
                className="parcel"
                id="parcel-chip"
                key={index}
                label={parcel}
                classes={{
                  deleteIcon: "parcel-delete-icon",
                  label: "parcel-chip-label",
                }}
                deleteable={"true"}
                disabled={
                  requestStatus !== "I" && requestStatus !== "IP" ? true : false
                }
                onDelete={() => {
                  let newParcelNumbers = parcelNumbersList.filter(
                    (parcelNumber) => parcelNumber !== parcel,
                  );
                  setParcelNumbersList(newParcelNumbers);

                  let newState = {
                    ...slide9,
                    parcelNumbers: newParcelNumbers.join(", "),
                  };
                  setSlide9(newState);

                  setIsLoading(true);

                  slide9Mutation.mutate(newState);
                }}
              />
            ))}
          </ResponsiveRow>
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default ProjectLocation;
